import { useEffect, useRef, useState, useCallback, useMemo } from 'react';
import { useDispatch } from 'react-redux';
import { Gauge, gaugeClasses } from '@mui/x-charts/Gauge';
import { getWaterUsageData } from '../../Redux/actions';

const POLL_INTERVAL_MS = 5 * 60 * 1000; // meters report every 5 min

const PULSE_STYLE = `
@keyframes pulse-ring {
    0%   { box-shadow: 0 0 0 0 rgba(46,125,50,0.55); }
    70%  { box-shadow: 0 0 0 7px rgba(46,125,50,0); }
    100% { box-shadow: 0 0 0 0 rgba(46,125,50,0); }
}
.wug-pulse { animation: pulse-ring 1.6s ease-in-out infinite; }
`;

function timeAgo(isoString) {
    if (!isoString) return '—';
    const diffMs = Date.now() - new Date(isoString).getTime();
    const diffMin = Math.round(diffMs / 60000);
    if (diffMin <= 0) return 'just now';
    if (diffMin === 1) return '1 min ago';
    return `${diffMin} min ago`;
}

const BudgetBar = ({ volumeKl, budgetKl, costInr }) => {
    const hasBudget = budgetKl > 0;
    const isOverflow = hasBudget && volumeKl > budgetKl;
    const pct = hasBudget ? (volumeKl / budgetKl) * 100 : 0;

    // Normal bar colour changes as usage approaches the limit
    const normalColor = pct >= 90 ? '#f57c00' : '#008080';

    // When over budget: teal segment = budget portion, red segment = excess
    // Both widths are % of the full bar (scaled to volumeKl)
    const normalWidth   = isOverflow ? (budgetKl / volumeKl) * 100 : Math.min(100, pct);
    const overflowWidth = isOverflow ? ((volumeKl - budgetKl) / volumeKl) * 100 : 0;

    const labelColor = isOverflow ? '#d32f2f' : hasBudget ? normalColor : '#bdbdbd';
    const labelText  = !hasBudget
        ? 'No budget set'
        : isOverflow
            ? `${pct.toFixed(1)}% — over budget`
            : `${pct.toFixed(1)}% of budget`;

    return (
        <div style={{ borderTop: '1px solid #f0f0f0', paddingTop: '8px' }}>
            {/* volume / budget label */}
            <div style={{ fontSize: '11px', color: '#555', textAlign: 'center', marginBottom: '5px' }}>
                <span style={{ fontWeight: 600, color: '#1565c0' }}>{volumeKl.toFixed(1)}</span>
                <span style={{ color: '#bdbdbd', margin: '0 4px' }}>/</span>
                <span style={{ fontWeight: 600 }}>{hasBudget ? budgetKl.toFixed(1) : '—'}</span>
                <span style={{ color: '#9e9e9e', marginLeft: '4px' }}>Kilo Liters</span>
            </div>

            {/* progress bar — splits into teal + red when over budget */}
            <div style={{
                height: '8px',
                background: '#eef0f2',
                borderRadius: '20px',
                overflow: 'hidden',
                marginBottom: '6px',
                border: '1px solid #e0e0e0',
            }}>
                <div style={{ display: 'flex', height: '100%' }}>
                    <div style={{
                        width: `${normalWidth}%`,
                        background: `linear-gradient(90deg, ${normalColor}bb, ${normalColor})`,
                        transition: 'width 0.4s ease',
                    }} />
                    {isOverflow && (
                        <div style={{
                            width: `${overflowWidth}%`,
                            background: 'linear-gradient(90deg, #ef5350bb, #d32f2f)',
                            transition: 'width 0.4s ease',
                        }} />
                    )}
                </div>
            </div>

            {/* % label + cost */}
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <span style={{ fontSize: '10px', color: labelColor, fontWeight: 600 }}>
                    {labelText}
                </span>
                <span style={{ fontSize: '10px', color: '#607d8b', fontWeight: 600 }}>
                    ₹ {costInr > 0 ? costInr.toLocaleString('en-IN', { maximumFractionDigits: 1 }) : '0.0'}
                </span>
            </div>
        </div>
    );
};

const MeterGaugeCard = ({ meter }) => {
    const isRunning = meter.status === 'running';
    const arcColor  = isRunning ? '#2e7d32' : '#e0e0e0';
    const textColor = isRunning ? '#1b5e20' : '#9e9e9e';

    return (
        <div style={{
            background: '#fff',
            borderRadius: '12px',
            border: `1px solid ${isRunning ? '#c8e6c9' : '#eeeeee'}`,
            boxShadow: isRunning
                ? '0 4px 14px rgba(46,125,50,0.13)'
                : '0 2px 8px rgba(0,0,0,0.06)',
            overflow: 'hidden',
            position: 'relative',
        }}>
            {/* top accent bar */}
            <div style={{
                height: '4px',
                background: isRunning
                    ? 'linear-gradient(90deg,#2e7d32,#66bb6a)'
                    : '#eeeeee',
            }} />

            {/* avg badge — absolute top-right; name row has matching paddingRight to avoid overlap */}
            {meter.avg_pct !== null && meter.avg_pct !== undefined && (
                <div style={{
                    position: 'absolute',
                    top: '14px',
                    right: '10px',
                    fontSize: '11px',
                    fontWeight: 700,
                    borderRadius: '20px',
                    padding: '2px 8px',
                    background: meter.avg_pct > 120 ? '#fde8e8'
                        : meter.avg_pct > 100 ? '#fff3e0'
                        : '#e8f5e9',
                    color: meter.avg_pct > 120 ? '#c62828'
                        : meter.avg_pct > 100 ? '#e65100'
                        : '#2e7d32',
                    border: `1px solid ${meter.avg_pct > 120 ? '#f9a8a8'
                        : meter.avg_pct > 100 ? '#ffcc80'
                        : '#a5d6a7'}`,
                    whiteSpace: 'nowrap',
                    zIndex: 1,
                }}>
                    {meter.avg_pct}% avg
                </div>
            )}

            <div style={{ padding: '12px 14px 14px' }}>
                {/* dot + name — paddingRight reserves space for the absolute badge */}
                <div style={{ display: 'flex', alignItems: 'flex-start', gap: '8px', marginBottom: '8px', paddingRight: '72px' }}>
                    <div
                        className={isRunning ? 'wug-pulse' : ''}
                        style={{
                            width: '9px', height: '9px',
                            borderRadius: '50%',
                            backgroundColor: isRunning ? '#2e7d32' : '#bdbdbd',
                            flexShrink: 0,
                            marginTop: '3px',
                        }}
                    />
                    <span style={{ fontSize: '12px', fontWeight: 600, color: '#333', lineHeight: 1.35 }}>
                        {meter.display_name}
                    </span>
                </div>

                {/* gauge */}
                <div style={{ width: '100%', height: '110px', margin: '0 auto' }}>
                    <Gauge
                        value={100}
                        valueMin={0}
                        valueMax={100}
                        startAngle={-110}
                        endAngle={110}
                        text={() => `${meter.flow_lpm.toFixed(1)}\nL/min`}
                        sx={{
                            [`& .${gaugeClasses.valueArc}`]: { fill: arcColor },
                            [`& .${gaugeClasses.referenceArc}`]: { fill: '#f5f5f5' },
                            [`& .${gaugeClasses.valueText}`]: {
                                fontSize: 15,
                                fontWeight: 700,
                                fill: textColor,
                            },
                        }}
                    />
                </div>

                {/* status pill */}
                <div style={{ textAlign: 'center', marginTop: '-2px', marginBottom: '10px' }}>
                    <span style={{
                        display: 'inline-block',
                        fontSize: '11px',
                        fontWeight: 600,
                        color: isRunning ? '#2e7d32' : '#9e9e9e',
                        background: isRunning ? '#f1f8f1' : '#fafafa',
                        border: `1px solid ${isRunning ? '#c8e6c9' : '#e0e0e0'}`,
                        borderRadius: '20px',
                        padding: '2px 12px',
                    }}>
                        {isRunning ? '● Running' : '○ Stopped'}
                    </span>
                </div>

                {/* volume block */}
                <div style={{
                    background: '#f8f9fa',
                    borderRadius: '8px',
                    padding: '8px 10px',
                    marginBottom: '8px',
                }}>
                    <div style={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                        marginBottom: '3px',
                    }}>
                        <span style={{ fontSize: '10px', color: '#9e9e9e', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                            Volume
                        </span>
                        <span style={{ fontSize: '10px', color: '#bdbdbd' }}>
                            {timeAgo(meter.last_updated)}
                        </span>
                    </div>
                    <div style={{ fontSize: '22px', fontWeight: 800, color: '#1565c0', lineHeight: 1 }}>
                        {Math.round(meter.volume_kl).toLocaleString()}
                        <span style={{ fontSize: '12px', fontWeight: 500, color: '#90a4ae', marginLeft: '4px' }}>KL</span>
                    </div>
                </div>

                {/* budget progress block */}
                <BudgetBar volumeKl={meter.volume_kl} budgetKl={meter.budget_kl} costInr={meter.cost_inr} />
            </div>
        </div>
    );
};

const MeterSection = ({ title, meters, gradientFrom, gradientTo }) => {
    const runningCount = meters.filter(m => m.status === 'running').length;
    const totalVolume  = meters.reduce((s, m) => s + (m.volume_kl || 0), 0);

    const meterRows = (() => {
        if (meters.length === 0) return [];
        const numRows = Math.ceil(meters.length / 6);
        const colsPerRow = Math.ceil(meters.length / numRows);
        return Array.from({ length: numRows }, (_, i) =>
            meters.slice(i * colsPerRow, i * colsPerRow + colsPerRow)
        );
    })();

    return (
        <div style={{ marginBottom: '28px' }}>
            {/* gradient header */}
            <div style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                background: `linear-gradient(135deg, ${gradientFrom}, ${gradientTo})`,
                borderRadius: '10px',
                padding: '12px 18px',
                marginBottom: '14px',
                flexWrap: 'wrap',
                gap: '10px',
            }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                    <span style={{ fontSize: '14px', fontWeight: 700, color: '#fff' }}>{title}</span>
                    <span style={{
                        fontSize: '11px',
                        background: 'rgba(255,255,255,0.2)',
                        color: '#fff',
                        borderRadius: '12px',
                        padding: '2px 9px',
                        fontWeight: 600,
                    }}>
                        {meters.length} meters
                    </span>
                </div>

                <div style={{ display: 'flex', alignItems: 'center', gap: '20px' }}>
                    <div style={{ textAlign: 'right' }}>
                        <div style={{ fontSize: '10px', color: 'rgba(255,255,255,0.72)', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                            Active
                        </div>
                        <div style={{ fontSize: '18px', fontWeight: 800, color: '#fff', lineHeight: 1.1 }}>
                            {runningCount}
                            <span style={{ fontSize: '12px', fontWeight: 400, opacity: 0.8 }}>/{meters.length}</span>
                        </div>
                    </div>
                    <div style={{ width: '1px', height: '34px', background: 'rgba(255,255,255,0.25)' }} />
                    <div style={{ textAlign: 'right' }}>
                        <div style={{ fontSize: '10px', color: 'rgba(255,255,255,0.72)', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                            Total Volume
                        </div>
                        <div style={{ fontSize: '18px', fontWeight: 800, color: '#fff', lineHeight: 1.1 }}>
                            {Math.round(totalVolume).toLocaleString()}
                            <span style={{ fontSize: '12px', fontWeight: 400, opacity: 0.8, marginLeft: '3px' }}>KL</span>
                        </div>
                    </div>
                </div>
            </div>

            {meterRows.length > 0 ? (
                <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                    {meterRows.map((row, i) => (
                        <div key={i} style={{
                            display: 'grid',
                            gridTemplateColumns: `repeat(${row.length}, 1fr)`,
                            gap: '12px',
                        }}>
                            {row.map(meter => <MeterGaugeCard key={meter.meter_id} meter={meter} />)}
                        </div>
                    ))}
                </div>
            ) : (
                <div style={{
                    textAlign: 'center',
                    padding: '32px',
                    color: '#bdbdbd',
                    fontSize: '13px',
                    background: '#fafafa',
                    borderRadius: '8px',
                    border: '1px dashed #e0e0e0',
                }}>
                    No consumption meters configured
                </div>
            )}
        </div>
    );
};

export default function WaterUsageGauges({ projectIds }) {
    const dispatch = useDispatch();
    const [usageData, setUsageData]   = useState({ fresh_water: [], recycled_water: [] });
    const [loading, setLoading]       = useState(false);
    const [lastRefresh, setLastRefresh] = useState(null);
    const intervalRef    = useRef(null);
    const hasLoadedRef   = useRef(false);

    const projectIdsRef = useRef(projectIds);
    projectIdsRef.current = projectIds;
    const projectIdsKey = useMemo(
        () => (projectIds && projectIds.length > 0 ? JSON.stringify(projectIds) : null),
        [projectIds]
    );

    const fetchUsageData = useCallback(() => {
        const ids = projectIdsRef.current;
        if (!ids || ids.length === 0) return;
        if (document.hidden) return;

        if (!hasLoadedRef.current) setLoading(true);
        dispatch(getWaterUsageData({ project_ids: ids }))
            .then((res) => {
                setLoading(false);
                if (res && res.status === 200 && res.data?.success) {
                    hasLoadedRef.current = true;
                    setUsageData(res.data.data || { fresh_water: [], recycled_water: [] });
                    setLastRefresh(new Date());
                }
            })
            .catch(() => { setLoading(false); });
    }, [dispatch]);

    useEffect(() => {
        if (!projectIdsKey) return;
        fetchUsageData();
        intervalRef.current = setInterval(fetchUsageData, POLL_INTERVAL_MS);
        const onVisible = () => { if (!document.hidden) fetchUsageData(); };
        document.addEventListener('visibilitychange', onVisible);
        return () => {
            clearInterval(intervalRef.current);
            document.removeEventListener('visibilitychange', onVisible);
        };
    }, [projectIdsKey, fetchUsageData]);

    if (loading) {
        return (
            <div style={{ textAlign: 'center', padding: '60px', color: '#bdbdbd' }}>
                <div style={{ fontSize: '14px', fontWeight: 500 }}>Loading live meter data…</div>
            </div>
        );
    }

    return (
        <div>
            <style>{PULSE_STYLE}</style>

            {/* top bar: live badge + last refresh */}
            <div style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                marginBottom: '20px',
            }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                    <div style={{
                        width: '8px', height: '8px', borderRadius: '50%',
                        background: '#2e7d32',
                        animation: 'pulse-ring 1.6s ease-in-out infinite',
                    }} />
                    <span style={{ fontSize: '12px', fontWeight: 700, color: '#2e7d32', textTransform: 'uppercase', letterSpacing: '0.6px' }}>
                        Live
                    </span>
                    <span style={{ fontSize: '12px', color: '#bdbdbd' }}>· Updates every 5 min</span>
                </div>
                {lastRefresh && (
                    <span style={{ fontSize: '11px', color: '#bdbdbd' }}>
                        Last refreshed: {lastRefresh.toLocaleTimeString()}
                    </span>
                )}
            </div>

            <MeterSection
                title="Fresh Water Consumption Meters"
                meters={usageData.fresh_water || []}
                gradientFrom="#43a047"
                gradientTo="#81c784"
            />
            <MeterSection
                title="Recycled Water Consumption Meters"
                meters={usageData.recycled_water || []}
                gradientFrom="#00897b"
                gradientTo="#4db6ac"
            />
        </div>
    );
}
